import styled, { css } from 'styled-components';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { ellipsisMultiline } from '@/styles/helpers';
import { SelectAppearance } from '@/components/UI/Select/Select.types';

const getAngleStyles = (appearance: SelectAppearance) => {
  switch (appearance) {
    case 'filled':
      return css`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;

        & > svg {
          height: 12px;
          width: 7px;
        }
      `;
    default:
      return css``;
  }
};

const getSelectBoxStyles = (
  appearance: SelectAppearance,
  isOpen?: boolean,
  hasError?: boolean
) => {
  switch (appearance) {
    case 'filled': {
      return css`
        width: 100%;
        min-height: 54px;
        padding: 0 15px;
        border: 1px solid ${({ theme }) => theme.palette.primary.border};
        border-radius: 14px;
        background-color: ${({ theme }) => theme.palette.quartenary.background};

        ${isOpen &&
        css`
          outline: none;
          border: 1px solid ${({ theme }) => theme.palette.brand.main};
          box-shadow: 5px 5px 12px 0 rgba(0, 0, 0, 0.05);
        `}

        ${hasError &&
        css`
          border: 1px solid ${({ theme }) => theme.palette.system.red};
          background-color: ${({ theme }) => theme.palette.system.redToWhite};
        `}
      `;
    }
    default: {
      return css`
        height: 60px;
      `;
    }
  }
};

export const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const SelectBox = styled.div<{
  name?: string;
  $hasBg?: boolean;
  $hasBorderBottom?: boolean;
  $appearance: SelectAppearance;
  $isOpen: boolean;
  $hasError?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;

  ${({ $hasBorderBottom }) =>
    $hasBorderBottom &&
    css`
      border-bottom: 1px solid ${({ theme }) => theme.palette.primary.border};
    `}

  ${({ $hasBg }) =>
    $hasBg &&
    css`
      background-color: ${({ theme }) => theme.palette.primary.background};
    `}

  img {
    object-fit: contain;
  }

  ${({ $appearance, $isOpen, $hasError }) =>
    getSelectBoxStyles($appearance, $isOpen, $hasError)}
`;

export const SelectValue = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SelectImgBox = styled.div`
  margin-right: 5px;
`;

export const SelectImgWithScrBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
  border-radius: 7px;
  background-color: ${({ theme }) => theme.palette.secondary.background};

  img {
    object-fit: contain;
  }
`;

export const SelectTexts = styled.div`
  width: 100%;
  position: relative;
  display: grid;
  align-items: center;
  min-height: 52px;
  padding: 27.5px 20px 8px 0;
`;

export const SelectLabel = styled(Typography.Title)<{
  $top: string;
  $hasError?: boolean;
}>`
  color: ${({ theme }) => theme.palette.secondary.labels};
  position: absolute;
  width: max-content;
  line-height: 16.5px;
  top: ${({ $top }) => $top};
  transition: top 0.3s ease-in-out;

  ${({ $hasError }) =>
    $hasError &&
    css`
      color: ${({ theme }) => theme.palette.system.red};
    `}
`;

export const SelectText = styled(Typography.Body)<{
  $isHidden: boolean;
  $maxLines: number;
}>`
  ${({ $maxLines }) => ellipsisMultiline($maxLines)}

  ${({ $isHidden }) =>
    $isHidden &&
    css`
      display: none;
    `};
`;

export const SelectAngle = styled.div<{ $appearance?: SelectAppearance }>`
  transform: rotate(180deg);

  ${({ $appearance = 'default' }) => getAngleStyles($appearance)}
`;

export const SelectSearchInput = styled.input`
  width: 100%;
  background-color: transparent;
  border: 0;
  outline: 0;
  font-size: 16px;
  font-style: normal;
  line-height: 16.5px;

  &::placeholder {
    color: ${({ theme }) => theme.palette.secondary.labels};
  }
`;
